import React, { useState } from 'react'
import {
  alpha,
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  FormControlLabel,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StageSection from './StageSection'
import RunProgress from './RunProgress'
import CostConfirmDialog, { CostConfirmDetails } from './CostConfirmDialog'
import TestRunStage, { responsesLabel, TestRunStageProps } from './TestRunStage'
import JunkCheckOffer, { JunkCheckOfferProps } from './JunkCheckOffer'
import JunkCheckConfirmDialog from './JunkCheckConfirmDialog'
import { effectiveScreening, JUNK_NOTHING_LEFT_REASON, JUNK_RUNNING_REASON, junkEstimateLabel } from '../../utils/themeAIJunk'
import JunkReviewStage, { JunkReviewStageProps } from './JunkReviewStage'
import PersonalDataReviewStage, { PersonalDataReviewStageProps } from './PersonalDataReviewStage'
import { formatBudgetLeft, formatEstimateMoney } from '../../utils/themeAICost'
import type { ThemeAIRunView } from '../../utils/themeAIStageProgress'
import type { ThemeAIResponseScope, ThemeAIRun, ThemeAIStandardCode, ThemeAIStandardFrames } from '../../types/gotypes'
import StandardCodeFrameDialog, { StandardFrameKind } from './StandardCodeFrameDialog'
import AiAccessTooltip from '../system/AiAccessTooltip'
import DisabledReason from '../system/DisabledReason'
import { themeAIPassOffReason } from '../../utils/permissionReason'

export interface ThemeAIRunOptions {
  scope: ThemeAIResponseScope
  includeExplanations: boolean
  includeSentiment: boolean
  includeEmotion: boolean
}

interface RunStageProps {
  poolSize: number
  frameName?: string
  frameVersion?: number
  themeCount: number
  /** The latest completed full run on the current data. When set, this stage is "Run again". */
  lastRun?: ThemeAIRun
  /** Responses on the current data with no codes yet (the "new only" scope). */
  uncodedCount?: number
  options: ThemeAIRunOptions
  onOptionsChange: (options: ThemeAIRunOptions) => void
  estimate?: CostConfirmDetails
  estimateLoading: boolean
  estimateError?: string
  pendingRun?: ThemeAIRun
  runningRun?: ThemeAIRun
  starting: boolean
  startError?: string
  onStartRun: () => Promise<void>
  onResumePendingRun: () => void
  onDiscardPendingRun: () => void
  onViewResults: () => void
  /** The standard sentiment and emotion codes, shown when switched on (spec #916 §9.3: visible, not editable). */
  standardFrames?: ThemeAIStandardFrames
  /** Run's own form, or its "Test on a sample" view. */
  view: ThemeAIRunView
  onViewChange: (view: ThemeAIRunView) => void
  /** Everything the test view needs; its ways back to Run are wired here. */
  test: Omit<TestRunStageProps, 'onContinue' | 'onBack'>
  /**
   * The junk check (#1891): its offer on Run, and the data its review view
   * needs. Starting a check and checking again are closed by `blockedReason`;
   * the decision and Keep by this stage's `writeBlockedReason`.
   */
  junk: Omit<JunkCheckOfferProps, 'onReview'>
    & Pick<JunkReviewStageProps, 'records' | 'category' | 'onCategoryChange' | 'onSetDecision' | 'onSetKept'>
  /**
   * The same check's personal data finding (#494): what its review view
   * needs. Optional so a page without it still renders the junk check.
   */
  personalData?: Pick<PersonalDataReviewStageProps,
    'records' | 'category' | 'onCategoryChange' | 'onSetDecision' | 'onSetKept' | 'onDownload' | 'downloading'>
  /** Why a full run is closed to this person (#1537 AI access, needs level 4), or undefined. */
  runBlockedReason?: string
  /** Why this person cannot change the task (discard a run that did not start), or undefined. */
  writeBlockedReason?: string
}

const describeOptions = (run: ThemeAIRun) =>
  [
    run.response_scope === 'new_only' ? 'new responses only' : 'every response',
    run.include_explanations && 'explanations',
    run.include_sentiment && 'sentiment',
    run.include_emotion && 'emotion',
  ].filter(Boolean).join(' · ')

// The full run, stated as one sentence the user can check before paying for
// it: what will be coded, with which frame, at what estimated cost. Extra
// outputs sit behind a disclosure; the confirmation dialog (spec #916 §7.3)
// is the go/no-go.
const RunStage: React.FC<RunStageProps> = ({
  poolSize,
  frameName,
  frameVersion,
  themeCount,
  lastRun,
  uncodedCount,
  options,
  onOptionsChange,
  estimate,
  estimateLoading,
  estimateError,
  pendingRun,
  runningRun,
  starting,
  startError,
  onStartRun,
  onResumePendingRun,
  onDiscardPendingRun,
  onViewResults,
  standardFrames,
  view,
  onViewChange,
  test,
  junk,
  personalData,
  runBlockedReason,
  writeBlockedReason,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  // "Check again" from the review: its own confirmation, since the review
  // replaces the offer that normally holds it.
  const [recheckOpen, setRecheckOpenState] = useState(false)
  const setRecheckOpen = (open: boolean) => {
    setRecheckOpenState(open)
    junk.onConfirmOpenChange(open)
  }
  // SKY-20 (#1234): a pass the customer has switched off is shown off and
  // disabled with the reason; the server refuses it too.
  const sentimentOff = standardFrames?.sentiment_off ? themeAIPassOffReason('sentiment') : undefined
  const emotionOff = standardFrames?.emotion_off ? themeAIPassOffReason('emotion') : undefined
  const [moreOpen, setMoreOpen] = useState(options.includeExplanations || options.includeSentiment || options.includeEmotion)
  const [frameOpen, setFrameOpen] = useState<StandardFrameKind>()

  const set = (patch: Partial<ThemeAIRunOptions>) => onOptionsChange({ ...options, ...patch })

  const handleProceed = async () => {
    try {
      await onStartRun()
    } catch {
      // The error shows on the page. If the run was created but did not
      // start, the page switches to the pending-run view, whose Start button
      // retries that same run.
    } finally {
      setConfirmOpen(false)
    }
  }

  const handleRecheck = async () => {
    try {
      await junk.onStartCheck()
      onViewChange('run')
    } catch {
      // The hook reports it; stay on the review.
    } finally {
      setRecheckOpen(false)
    }
  }

  const frameLine = frameName
    ? `with “${frameName}”${frameVersion ? ` v${frameVersion}` : ''} · ${themeCount} theme${themeCount === 1 ? '' : 's'}`
    : `${themeCount} theme${themeCount === 1 ? '' : 's'}`

  if (runningRun) {
    return (
      <StageSection title={`Run #${runningRun.run_number} is coding`} description={`${runningRun.response_count.toLocaleString()} responses ${frameLine}.`}>
        <Box>
          <RunProgress run={runningRun} label={`Coding ${runningRun.response_count.toLocaleString()} responses…`} />
          <Button color="info" sx={{ mt: 2, ml: -1 }} onClick={onViewResults}>Go to results</Button>
        </Box>
      </StageSection>
    )
  }

  if (pendingRun) {
    return (
      <StageSection
        title={`Run #${pendingRun.run_number} has not started`}
        description="It was created but did not start — usually a budget or connection problem. Start it again, or discard it to choose different settings."
        actions={
          <>
            <DisabledReason reason={writeBlockedReason}>
              <Button color="info" onClick={onDiscardPendingRun} disabled={starting}>Discard</Button>
            </DisabledReason>
            <AiAccessTooltip reason={runBlockedReason}>
              <Button variant="contained" color="info" onClick={onResumePendingRun} disabled={starting || !!runBlockedReason}>
                {starting ? 'Starting…' : `Start run #${pendingRun.run_number}`}
              </Button>
            </AiAccessTooltip>
          </>
        }
      >
        {startError && <Alert severity="error">{startError}</Alert>}
        <Typography variant="body2" color="text.secondary">
          {pendingRun.response_count.toLocaleString()} responses · {describeOptions(pendingRun)}
        </Typography>
      </StageSection>
    )
  }

  if (view === 'test') {
    return <TestRunStage {...test} onContinue={() => onViewChange('run')} onBack={() => onViewChange('run')} />
  }

  // Review acts on the check runs use, which is the older one while a newer
  // check runs or after it failed.
  const reviewable = effectiveScreening(junk.screening)
  if (view === 'junk' && reviewable) {
    return (
      <>
        <JunkReviewStage
          key={reviewable.id}
          screening={reviewable}
          records={junk.records}
          category={junk.category}
          onCategoryChange={junk.onCategoryChange}
          saving={junk.starting}
          writeBlockedReason={writeBlockedReason}
          checkBlockedReason={junk.blockedReason ?? (junk.screening?.status === 'running' || junk.screening?.status === 'pending' ? 'A junk check is already running.' : undefined)}
          onSetDecision={junk.onSetDecision}
          onSetKept={junk.onSetKept}
          onCheckAgain={() => setRecheckOpen(true)}
          onDiscard={async () => { await junk.onDiscard() }}
          onBack={() => onViewChange('run')}
        />
        <JunkCheckConfirmDialog
          open={recheckOpen}
          loading={junk.estimateLoading}
          starting={junk.starting}
          details={junk.estimate}
          onCancel={() => !junk.starting && setRecheckOpen(false)}
          onProceed={handleRecheck}
        />
      </>
    )
  }

  if (view === 'personal-data' && reviewable && personalData) {
    return (
      <PersonalDataReviewStage
        key={reviewable.id}
        screening={reviewable}
        {...personalData}
        saving={junk.starting}
        writeBlockedReason={writeBlockedReason}
        onBack={() => onViewChange('run')}
      />
    )
  }

  // A run started while the check runs would pay to code what the check is
  // there to catch, so Review and run waits for it.
  const checkRunning = junk.screening?.status === 'pending' || junk.screening?.status === 'running'
  const reviewBlockedReason = runBlockedReason
    ?? (checkRunning ? JUNK_RUNNING_REASON : undefined)
  // A run of nothing is an empty "completed" run. Junk coded as 99 is real
  // work, so only a drop with no answers left closes it.
  const nothingLeft = Boolean(estimate) && estimate?.responseCount === 0
    && (!estimate?.junkExcluded || estimate.junkDecision === 'drop')
  const runBlocked = reviewBlockedReason
    ?? (nothingLeft && estimate?.junkExcluded ? JUNK_NOTHING_LEFT_REASON : undefined)

  const frameChanged = Boolean(lastRun && (lastRun.code_frame_id !== undefined) && frameVersion !== undefined
    && lastRun.code_frame_version !== frameVersion)

  return (
    <StageSection
      title={lastRun ? 'Run again' : 'Code every response'}
      description={
        lastRun
          ? `Run #${lastRun.run_number} coded ${lastRun.response_count.toLocaleString()} responses${lastRun.completed_at ? ` on ${new Date(lastRun.completed_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : ''}. Choose what to code this time.`
          : 'Check what will be coded and what it will spend. You confirm before anything is spent.'
      }
    >
      {startError && <Alert severity="error">{startError}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 640 }}>
        <JunkCheckOffer
          {...junk}
          writeBlockedReason={writeBlockedReason}
          onReview={() => onViewChange('junk')}
          onReviewPersonalData={personalData ? () => onViewChange('personal-data') : undefined}
        />
        <TestOffer
          testRun={test.testRun}
          testing={test.testing}
          sampleSize={test.sampleSize}
          blockedReason={test.blockedReason}
          onOpenTest={() => onViewChange('test')}
        />
        {lastRun ? (
          <Box role="radiogroup" aria-label="What to code" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <ScopeCard
              selected={options.scope === 'new_only'}
              disabled={uncodedCount === 0}
              title={uncodedCount === 0 ? 'Nothing new to code' : `Code the ${uncodedCount === undefined ? '' : `${uncodedCount.toLocaleString()} `}responses without codes`}
              detail={uncodedCount === 0 ? 'Every response already has codes.' : 'Everything already coded stays exactly as it is.'}
              onSelect={() => set({ scope: 'new_only' })}
            />
            <ScopeCard
              selected={options.scope === 'full_refresh'}
              title={`Recode all ${poolSize.toLocaleString()} responses`}
              detail="Replaces every earlier code with the current code frame."
              onSelect={() => set({ scope: 'full_refresh' })}
            />
          </Box>
        ) : (
          <Typography variant="h5" component="p" sx={{ fontWeight: 600 }}>
            {poolSize.toLocaleString()} responses
          </Typography>
        )}
        {frameChanged && (
          <Typography variant="body2" color="text.secondary">
            The code frame has changed since run #{lastRun?.run_number}. Coding only the responses without codes keeps
            earlier results on the old frame; recoding everything applies the new frame to all of them.
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary">{frameLine}</Typography>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', py: 1.5, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
          {lastRun && <Figure label="Responses" value={estimate?.responseCount.toLocaleString()} loading={estimateLoading} />}
          {Boolean(estimate?.junkExcluded) && (
            <Figure label={junkEstimateLabel(estimate?.junkDecision)} value={estimate?.junkExcluded?.toLocaleString()} loading={estimateLoading} />
          )}
          <Figure label="Estimated budget spend" value={estimate ? formatEstimateMoney(estimate.estimatedCost, estimate.moneyUnit) : undefined} loading={estimateLoading} strong />
          <Figure label="Estimated tokens" value={estimate ? `~${estimate.estimatedTokens.toLocaleString()}` : undefined} loading={estimateLoading} />
          {typeof estimate?.remainingBudget === 'number' && (
            <Figure label="Budget left" value={formatBudgetLeft(estimate.remainingBudget, estimate.moneyUnit)} loading={estimateLoading} />
          )}
        </Box>
        {estimateError && <Typography variant="caption" color="text.secondary">{estimateError}</Typography>}

        <Box>
          <Button
            size="small"
            color="info"
            onClick={() => setMoreOpen((open) => !open)}
            endIcon={<ExpandMoreIcon sx={{ transform: moreOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease-out' }} />}
            aria-expanded={moreOpen}
            sx={{ ml: -1 }}
          >
            Explanations, sentiment and emotion
          </Button>
          <Collapse in={moreOpen}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
              <OptionSwitch
                checked={options.includeExplanations}
                onChange={(value) => set({ includeExplanations: value })}
                label="Explain each assignment"
                hint="Useful for checking the coding. Roughly doubles what the run spends."
              />
              <OptionSwitch
                checked={options.includeSentiment && !sentimentOff}
                onChange={(value) => set({ includeSentiment: value })}
                label="Add sentiment"
                hint="Only switch on where the client has approved sentiment and emotion coding."
                disabledReason={sentimentOff}
              />
              <StandardCodes open={options.includeSentiment && !sentimentOff} codes={standardFrames?.sentiment} name="sentiment" onView={() => setFrameOpen('sentiment')} />
              <OptionSwitch
                checked={options.includeEmotion && !emotionOff}
                onChange={(value) => set({ includeEmotion: value })}
                label="Add emotion"
                disabledReason={emotionOff}
              />
              <StandardCodes open={options.includeEmotion && !emotionOff} codes={standardFrames?.emotion} name="emotion" onView={() => setFrameOpen('emotion')} />
            </Box>
          </Collapse>
        </Box>

        <Box>
          <AiAccessTooltip reason={runBlocked}>
            <Button
              variant="contained"
              color="info"
              size="large"
              disabled={themeCount === 0 || starting || estimateLoading || !estimate || nothingLeft || !!runBlocked}
              onClick={() => setConfirmOpen(true)}
            >
              Review and run
            </Button>
          </AiAccessTooltip>
        </Box>
      </Box>

      <StandardCodeFrameDialog
        kind={frameOpen}
        codes={frameOpen ? standardFrames?.[frameOpen] : undefined}
        description={frameOpen ? standardFrames?.[`${frameOpen}_description`] : undefined}
        onClose={() => setFrameOpen(undefined)}
      />
      <CostConfirmDialog
        open={confirmOpen}
        loading={starting}
        details={estimate ? { ...estimate, themeCount } : null}
        onCancel={() => !starting && setConfirmOpen(false)}
        onProceed={handleProceed}
      />
    </StageSection>
  )
}

// The test is a question Run asks, not a step the user must pass (Adam, 23
// Sep: "someone might not want to run a test every time ... as part of run,
// there's a, do you want to test this before you do it?"). Once a test on
// the current data and frame is done, it shrinks to one line.
const TestOffer: React.FC<{
  testRun?: ThemeAIRun
  testing: boolean
  /** The size just asked for. Used until the new test run appears in the runs poll. */
  sampleSize: number
  /** View and Test again both only open the test view. Test again spends
   *  nothing by itself: the picker there is pre-set to the last size, and its
   *  "Test N responses" button starts the test. One click here must not
   *  re-code a whole-pool "test" with no picker in view. */
  onOpenTest: () => void
  /** Why testing is closed (#1537). View stays open; starting a new test does not. */
  blockedReason?: string
}> = ({ testRun, testing, sampleSize, onOpenTest, blockedReason }) => {
  if (testing) {
    // Right after "Test again", testRun is still the previous, finished test:
    // the runs poll has not seen the new one yet, so its size would be wrong.
    const newRunVisible = testRun?.status === 'pending' || testRun?.status === 'running'
    const inFlightSize = newRunVisible ? testRun.response_count : sampleSize
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Testing a sample{inFlightSize ? ` of ${responsesLabel(inFlightSize)}` : ''}…
        </Typography>
        <Button size="small" color="info" onClick={onOpenTest}>View</Button>
      </Box>
    )
  }

  if (testRun?.status === 'completed') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Tested on {testRun.response_count.toLocaleString()} sampled response{testRun.response_count === 1 ? '' : 's'}.
        </Typography>
        <Button size="small" color="info" onClick={onOpenTest}>View</Button>
        <AiAccessTooltip reason={blockedReason}>
          <Button size="small" color="info" onClick={onOpenTest} disabled={!!blockedReason}>Test again</Button>
        </AiAccessTooltip>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>Do you want to test this before running?</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        Recommended the first few times. Nothing is written to your data.
      </Typography>
      <AiAccessTooltip reason={blockedReason}>
        <Button variant="outlined" color="info" onClick={onOpenTest} disabled={!!blockedReason}>Test on a sample</Button>
      </AiAccessTooltip>
    </Box>
  )
}

// One of the two re-run choices, as a selectable card rather than a radio
// buried in a form (Adam, 23 Sep: "a separate re-run UI").
const ScopeCard: React.FC<{ selected: boolean; disabled?: boolean; title: string; detail: string; onSelect: () => void }> = ({
  selected,
  disabled,
  title,
  detail,
  onSelect,
}) => (
  <ButtonBase
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    sx={(theme) => ({
      display: 'block',
      textAlign: 'left',
      p: 2,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: selected ? 'info.main' : 'divider',
      backgroundColor: selected ? alpha(theme.palette.info.main, 0.06) : 'transparent',
      opacity: disabled ? 0.5 : 1,
      transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
      '&:hover': { borderColor: selected ? 'info.main' : 'text.secondary' },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 2 },
    })}
  >
    <Typography variant="body1" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{detail}</Typography>
  </ButtonBase>
)

// The standard codes a switched-on sentiment or emotion pass assigns. They are
// the same for every customer (spec #916 §9.2), so they are shown, not edited:
// named here, and the whole code frame opens in a modal.
const StandardCodes: React.FC<{ open: boolean; codes?: ThemeAIStandardCode[]; name: string; onView: () => void }> = ({
  open,
  codes,
  name,
  onView,
}) => (
  <Collapse in={open && Boolean(codes?.length)} unmountOnExit>
    <Box sx={{ ml: 6, mb: 1, maxWidth: 520, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary">
        Uses the standard {name} code frame: {(codes || []).map((code) => code.text.charAt(0).toUpperCase() + code.text.slice(1)).join(', ')}.
      </Typography>
      <Button size="small" color="info" onClick={onView} sx={{ px: 0.5, minWidth: 0 }}>
        View code frame
      </Button>
    </Box>
  </Collapse>
)

const Figure: React.FC<{ label: string; value?: string; loading: boolean; strong?: boolean }> = ({ label, value, loading, strong }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    {loading && !value ? (
      <Skeleton width={64} height={28} />
    ) : (
      <Typography variant="body1" sx={{ fontWeight: strong ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>
        {value ?? '—'}
      </Typography>
    )}
  </Box>
)

const OptionSwitch: React.FC<{ checked: boolean; onChange: (value: boolean) => void; label: string; hint?: string; disabledReason?: string }> = ({
  checked,
  onChange,
  label,
  hint,
  disabledReason,
}) => (
  <FormControlLabel
    control={(
      <DisabledReason reason={disabledReason}>
        <Switch color="info" checked={checked} onChange={(event) => onChange(event.target.checked)} inputProps={{ 'aria-label': label }} />
      </DisabledReason>
    )}
    label={
      <Box>
        <Typography variant="body2">{label}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </Box>
    }
    sx={{ alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.5 } }}
  />
)

export default RunStage
